import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  Button,
  LoadingSpinner,
  TextInput,
  PlainButton,
  Tooltip,
} from "@jugl-web/ui-kit";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { collapsedIconButtonClassName } from "@web-src/modules/client-support/pages/ClientSupportPage/consts";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ManageAccountsDrawerScreenToParametersMap } from "../../../../types";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as WarningIcon } from "./assets/warning.svg";

export const VerifyEmailForwardingStep: React.FC<{
  forwardingEmail: string;
}> = ({ forwardingEmail }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const [sendVerificationEmail, { isLoading }] =
    integrationsApi.useSendVerificationEmailMutation();
  const { copyToClipboard } = useClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const [showVerificationError, setShowVerificationError] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isCopied]);

  const setupSteps = useMemo(
    () => [
      {
        id: "copy-inbox-email",
        text: t({
          id: "client-support-page.email-forwarding-step-copy-inbox-email",
          defaultMessage: "Copy your Jugl Inbox email address below",
        }),
      },
      {
        id: "go-to-domain-settings",
        text: t({
          id: "client-support-page.email-forwarding-step-go-to-domain-settings",
          defaultMessage: "Go to your Domain provider's general settings",
        }),
      },
      {
        id: "open-forwarding-settings",
        text: t({
          id: "client-support-page.email-forwarding-step-open-forwarding-settings",
          defaultMessage: "Open Email Forwarding settings",
        }),
      },
      {
        id: "paste-forwarding-address",
        text: t({
          id: "client-support-page.email-forwarding-step-paste-forwarding-address",
          defaultMessage:
            "Paste the Jugl Inbox email as a forwarding address and save",
        }),
      },
      {
        id: "check-inbox-confirmation",
        text: t({
          id: "client-support-page.email-forwarding-step-check-inbox-confirmation",
          defaultMessage:
            "Additionally check your inbox - your domain provider may require email confirmation",
        }),
      },
      {
        id: "return-and-verify",
        text: t({
          id: "client-support-page.email-forwarding-step-return-and-verify",
          defaultMessage: "Return to Jugl and click Verify button",
        }),
      },
    ],
    [t]
  );

  const handleCopy = useCallback(async () => {
    try {
      await copyToClipboard(
        forwardingEmail,
        t({
          id: "client-support-page.jugl-inbox-email-copied-to-clipboard",
          defaultMessage: "Your Jugl Inbox email was copied to clipboard",
        })
      );
      setIsCopied(true);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [copyToClipboard, forwardingEmail, t, toast]);

  const handleVerify = useCallback(async () => {
    try {
      setShowVerificationError(false);

      const result = await sendVerificationEmail({ entityId }).unwrap();

      if (result.metadata?.email_forwarding_verified) {
        toast(
          t({
            id: "client-support-page.email-account-connected",
            defaultMessage: "Email was successfully connected",
          })
        );
        transitionTo({ name: "emailAccount" });
        return;
      }

      setShowVerificationError(true);
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    }
  }, [entityId, sendVerificationEmail, t, toast, transitionTo]);

  return (
    <div className="flex max-w-150 flex-col gap-10">
      {showVerificationError && (
        <div className="flex items-center gap-3 rounded-xl bg-tertiary/5 p-3">
          <WarningIcon />
          <span className="max-w-125 font-secondary text-[13px] leading-[150%] text-dark-900">
            {t({
              id: "client-support-page.email-forwarding-not-detected",
              defaultMessage:
                "We couldn't detect email forwarding to your Jugl Inbox email. Check your Domain provider settings and try again",
            })}
          </span>
        </div>
      )}
      <div className="flex gap-4">
        <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-lg font-medium text-dark">
              {t({
                id: "client-support-page.verify-email-forwarding",
                defaultMessage: "Verify email forwarding",
              })}
            </span>
            <span className="font-secondary text-[13px] leading-[150%] text-dark-700">
              {t({
                id: "client-support-page.verify-email-forwarding-description",
                defaultMessage:
                  "To receive emails in your Jugl Inbox, forward incoming emails from your Domain provider to your personal Jugl email address below",
              })}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-secondary text-[13px] font-medium text-dark">
              {t({
                id: "client-support-page.how-to-set-it-up",
                defaultMessage: "ℹ️ How to set it up:",
              })}
            </span>

            <div className="flex flex-col gap-1">
              {setupSteps.map((step, index) => (
                <div key={step.id} className="flex items-center gap-2">
                  <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-[#2F80ED] font-secondary text-[10px] font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className="font-secondary text-[13px] leading-[160%] text-dark-700">
                    {step.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <TextInput
        labelPosition="border-overlap"
        label={t({
          id: "client-support-page.your-jugl-inbox-email",
          defaultMessage: "Your Jugl Inbox email",
        })}
        value={forwardingEmail}
        isDisabled
        endContent={
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                onClick={handleCopy}
                ref={ref}
                {...props}
                disabled={isCopied || !forwardingEmail}
              >
                {isCopied ? <CheckIcon /> : <CopyIcon />}
              </PlainButton>
            )}
          >
            {isCopied
              ? t({
                  id: "common.copied",
                  defaultMessage: "Copied",
                })
              : t({
                  id: "common.copy",
                  defaultMessage: "Copy",
                })}
          </Tooltip>
        }
        classNames={{
          wrapperClassName: "bg-grey-100",
          endContentWrapperClassName: "bg-grey-100",
        }}
      />

      <Button
        variant="primary"
        iconEnd={
          isLoading ? (
            <LoadingSpinner size="xs" color="grey" />
          ) : (
            <ArrowRightIcon />
          )
        }
        onClick={handleVerify}
        isDisabled={!forwardingEmail || isLoading}
        className="w-70"
      >
        {isLoading
          ? t({
              id: "client-support-page.processing",
              defaultMessage: "Processing...",
            })
          : t({
              id: "common.verify",
              defaultMessage: "Verify",
            })}
      </Button>
    </div>
  );
};
